import { useEffect, useRef, useState } from "react";
import { Check, CircleAlert, FileUp, FolderOpen, Loader2, Upload, X } from "lucide-react";
import { WsDropdown } from "src/stage/WsControls";
import { cancelUpload, completeUpload, documentError, formatBytes, initiateUpload, uploadToStorage, validateDocumentFile } from "./documentService";
import { buttonClass, DocumentError, primaryClass } from "./DocumentUI";

export default function DocumentUploadPanel({ workspaceId, leadId, leadName, categories, options, onUploaded, incoming = null }) {
  const [jobs, setJobs] = useState([]);
  const [categoryId, setCategoryId] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [dragging, setDragging] = useState(false);
  const mounted = useRef(true);
  const controllers = useRef(new Map());
  const fileInput = useRef(null);
  const running = useRef(false);
  const queueCancelled = useRef(new Set());
  const canStartNew = options.storage_configured !== false;
  const isRecording = (job, result) => ["audio", "video"].includes(result?.data?.kind)
    || /\.(?:m4a|mp3|mp4|mov|ogg|wav|webm)$/i.test(job.file.name);

  useEffect(() => {
    mounted.current = true;
    const activeControllers = controllers.current;
    return () => { mounted.current = false; activeControllers.forEach((controller) => controller.abort()); };
  }, []);
  useEffect(() => {
    if (!busy) return undefined;
    const warn = (event) => { event.preventDefault(); event.returnValue = ""; };
    window.addEventListener("beforeunload", warn);
    return () => window.removeEventListener("beforeunload", warn);
  }, [busy]);

  const update = (id, changes) => { if (mounted.current) setJobs((current) => current.map((job) => job.id === id ? { ...job, ...changes } : job)); };
  const discard = async (job) => {
    queueCancelled.current.add(job.id);
    const controller = controllers.current.get(job.id);
    if (controller) { controller.abort(); return; }
    if (job.documentId) {
      try { await cancelUpload(workspaceId, job.documentId); }
      catch (error) {
        if (error?.response?.status === 409) { update(job.id, { status: "ready", progress: 100, error: "" }); if (mounted.current) onUploaded(isRecording(job) ? [job.documentId] : []); return; }
        queueCancelled.current.delete(job.id);
        update(job.id, { status: "error", error: documentError(error) });
        return;
      }
    }
    if (mounted.current) {
      if (running.current) update(job.id, { status: "cancelled", error: "Upload cancelled." });
      else setJobs((current) => current.filter((item) => item.id !== job.id));
    }
  };
  const addFiles = (files) => {
    if (busy || !canStartNew) return;
    setError("");
    const incoming = Array.from(files);
    if (incoming.length + jobs.length > 50) { setError("Choose up to 50 files per upload batch."); return; }
    setJobs((current) => [...current, ...incoming.map((file) => {
      const error = validateDocumentFile(file, options);
      return { id: crypto.randomUUID(), file, categoryId, status: error ? "invalid" : "queued", error, progress: 0 };
    })]);
  };

  // Files dropped on the page (outside this panel) join the queue.
  useEffect(() => { if (incoming?.files?.length) addFiles(incoming.files); }, [incoming]); // eslint-disable-line react-hooks/exhaustive-deps

  const start = async () => {
    if (running.current) return;
    running.current = true; setBusy(true); setError("");
    const queue = jobs.filter((job) => ["queued", "error"].includes(job.status) && (canStartNew || job.documentId));
    let next = 0;
    let changed = false;
    const completedRecordings = new Set();
    const worker = async () => {
      while (next < queue.length && mounted.current) {
        const job = queue[next++];
        if (queueCancelled.current.has(job.id)) continue;
        const controller = new AbortController(); controllers.current.set(job.id, controller);
        let documentId = job.documentId; let uploaded = job.uploaded;
        update(job.id, { status: "uploading", error: "" });
        try {
          const result = await initiateUpload(workspaceId, { client_upload_id: job.id, file_name: job.file.name, size_bytes: job.file.size,
            category_id: job.categoryId || null, lead_id: leadId ? Number(leadId) : null });
          documentId = result.data.id;
          update(job.id, { documentId });
          if (controller.signal.aborted) throw new DOMException("Upload cancelled", "AbortError");
          if (result.data.status !== "ready") {
            if (!uploaded) {
              await uploadToStorage(result.upload, job.file, controller.signal, (progress) => update(job.id, { progress }));
              uploaded = true; update(job.id, { uploaded: true });
            }
            update(job.id, { status: "verifying", progress: 100 });
            const completed = await completeUpload(workspaceId, documentId);
            if (isRecording(job, completed)) completedRecordings.add(documentId);
          } else if (isRecording(job, result)) {
            completedRecordings.add(documentId);
          }
          update(job.id, { status: "ready", progress: 100 });
          changed = true;
        } catch (requestError) {
          if (controller.signal.aborted) {
            if (documentId) {
              try { await cancelUpload(workspaceId, documentId); }
              catch (cancelError) {
                // Completion may have won the race. Refresh instead of claiming a
                // saved document was removed by cancellation.
                if (cancelError?.response?.status === 409) {
                  update(job.id, { status: "ready", progress: 100 });
                  changed = true;
                  if (isRecording(job)) completedRecordings.add(documentId);
                  continue;
                }
              }
            }
            update(job.id, { status: "cancelled", error: "Upload cancelled." });
          } else update(job.id, { status: "error", documentId, uploaded, error: documentError(requestError) });
        } finally { controllers.current.delete(job.id); }
      }
    };
    await Promise.all(Array.from({ length: Math.min(3, queue.length) }, worker));
    running.current = false;
    if (mounted.current) { setBusy(false); if (changed) onUploaded([...completedRecordings]); }
  };

  const pending = jobs.filter((job) => ["queued", "error"].includes(job.status) && (canStartNew || job.documentId)).length;
  const done = jobs.filter((job) => job.status === "ready").length;
  const categoryOptions = [{ value: "", label: "Uncategorized" }, ...categories.filter((c) => c.is_active).map((c) => ({ value: c.id, label: c.name }))];
  const stateLabel = (job) => (job.status === "ready" ? "Uploaded" : job.status === "verifying" ? "Verifying file…" : job.status === "uploading" ? `Uploading ${job.progress}%` : job.status === "invalid" ? "Can’t upload" : job.status === "cancelled" ? "Cancelled" : job.status === "error" ? "Failed" : "Ready to upload");
  return <section aria-label="Upload documents" className="crm-upload">
    <div className="crm-upload-head">
      <div className="ws-grow"><h3 className="ws-h3">{leadId ? `Upload to ${leadName || `lead #${leadId}`}` : "Upload files"}</h3><p className="ws-muted">{leadId ? "Files will be linked to this lead." : "New files wait in “Waiting for a lead”. Link them when you’re ready; Appy can match recordings for you."}</p></div>
      <WsDropdown label="Default category" icon={FolderOpen} value={categoryId} onChange={setCategoryId} options={categoryOptions} align="right" />
    </div>
    <input ref={fileInput} type="file" multiple aria-label="Choose document files" accept={options.extensions.map((extension) => `.${extension}`).join(",")} className="ws-sr-only" disabled={busy || !canStartNew} onChange={(e) => { addFiles(e.target.files); e.target.value = ""; }} />
    <button type="button" disabled={busy || !canStartNew} onClick={() => fileInput.current?.click()} onDragOver={(e) => { e.preventDefault(); if (!busy && canStartNew) setDragging(true); }} onDragLeave={() => setDragging(false)} onDrop={(e) => { e.preventDefault(); e.stopPropagation(); setDragging(false); addFiles(e.dataTransfer.files); }}
      className={`crm-upload-drop${dragging ? " is-over" : ""}`}>
      <span className="crm-upload-drop-icon" aria-hidden="true"><FileUp size={22} /></span>
      <b>Choose files or drop them here</b>
      <span>{options.storage_provider === "cloudinary"
        ? "Cloudinary · Up to 10 MB images/documents · 100 MB audio/video (workspace limits may be lower)"
        : `Audio, video, documents and images · Up to ${formatBytes(options.max_bytes)} per file · ${options.extensions.slice(0, 8).map((extension) => extension.toUpperCase()).join(", ")}${options.extensions.length > 8 ? "…" : ""}`}</span>
    </button>
    <DocumentError message={error} />
    {jobs.length > 0 && <div className="crm-upload-queue">
      <ul aria-label="Upload queue">{jobs.map((job) => <li key={job.id} className={`crm-upload-job is-${job.status}`}>
        <span className="crm-upload-job-icon" aria-hidden="true">{job.status === "ready" ? <Check size={15} /> : ["invalid", "error"].includes(job.status) ? <CircleAlert size={15} /> : <FileUp size={15} />}</span>
        <div className="crm-upload-job-main">
          <p className="crm-upload-job-name">{job.file.name}</p>
          <p className="crm-upload-job-meta">{formatBytes(job.file.size)} · {stateLabel(job)}</p>
          {["uploading", "verifying"].includes(job.status) && <progress aria-label={`Progress for ${job.file.name}`} max={100} value={job.progress} className="crm-upload-progress" />}
          {job.error && <p className="crm-upload-job-error">{job.error}</p>}
        </div>
        {["queued", "error"].includes(job.status) && !job.documentId && !busy && <WsDropdown label={`Category for ${job.file.name}`} value={job.categoryId} onChange={(value) => update(job.id, { categoryId: value })} options={categoryOptions} align="right" />}
        {job.status === "ready" ? <Check aria-label="Uploaded" className="crm-upload-done" size={18} /> : <button type="button" aria-label={`${busy ? "Cancel" : "Remove"} ${job.file.name}`} onClick={() => discard(job)} className="crm-icon-btn crm-icon-btn--sm"><X size={14} /></button>}
      </li>)}</ul>
      <div className="crm-upload-foot"><span className="ws-muted" aria-live="polite">{done} of {jobs.length} uploaded</span><div className="crm-upload-foot-actions"><button type="button" disabled={busy} onClick={() => setJobs((all) => all.filter((job) => !["ready", "cancelled", "invalid"].includes(job.status)))} className={buttonClass}>Clear finished</button><button type="button" onClick={start} disabled={busy || !pending} className={primaryClass}>{busy ? <Loader2 size={14} className="ws-spin" /> : <Upload size={14} />}{busy ? "Uploading…" : `Upload / retry ${pending} file${pending === 1 ? "" : "s"}`}</button></div></div>
    </div>}
  </section>;
}
